import React, { useCallback, useEffect, useMemo } from 'react';

import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import AccountLayout from 'components/layout/account';
import LoadingAnimation from 'components/loading-animation';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { SecondaryIdentifierType } from 'generated/graphql-gateway';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { ShareApiData, useShareApi } from 'hooks/use-share-api/use-share-api';
import { withAuthRequired } from 'pages/authentication/auth-required';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { fullBrandName } from 'utils/environment';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import {
  CardDescription,
  InformationCard,
  InformationCardImage,
  InformationCardImageWrapper,
  InformationCardTextsContainer,
  InformationCardTitle,
  InvitationCodeNumberContainer,
  InvitationCodeNumberCopyIcon,
  InvitationCodeNumberText,
  ShareMyCodeButton,
  TermsAndConditions,
} from './styled';

const AccountIvitationCode = () => {
  const { formatMessage } = useIntl();
  const { loyaltyUser } = useLoyaltyUser();
  const theme = useTheme();
  const { canShare, shareData } = useShareApi();
  const navigate = useNavigate();
  const mParticle = useMParticleContext();
  const { featureInvitationCode, featureInvitationCodeLoading } = useFeatureInvitationCode();

  const userInvitationCode = useMemo(() => {
    const SecondaryInvitationCodeIdentifier = loyaltyUser?.secondaryIdentifiers?.find(
      identifier => identifier?.identifierType === SecondaryIdentifierType.INVITATIONCODE
    );

    if (!SecondaryInvitationCodeIdentifier) {
      toast.error(formatMessage({ id: 'shareInvitationCodeGeneralError' }));
      return;
    }

    return SecondaryInvitationCodeIdentifier?.identifier?.toUpperCase();
  }, [loyaltyUser, formatMessage]);

  const handleCopyInvitationCode = useCallback(() => {
    toast.dark(formatMessage({ id: 'invitationCodeCopiedText' }));
    mParticle.logNavigationClick(CustomEventNames.INVITATION_CODE_COPIED);
  }, [formatMessage]);

  const handleShareInvitationCode = useCallback(async () => {
    const signUpUrl = `${window.location.origin}${routes.signUp}`;

    const data: ShareApiData = {
      text: formatMessage(
        { id: 'shareInvitationCodeMessage' },
        { invitationCode: userInvitationCode, brand: fullBrandName() }
      ),
      title: fullBrandName(),
      url: signUpUrl,
    };

    try {
      await shareData(data);
      mParticle.logNavigationClick(CustomEventNames.INVITATION_CODE_SHARED);
    } catch (e) {
      // if the user abort the operation do not trigger the toast
      const isAbortError = e.toString().includes('AbortError');
      if (isAbortError) {
        return;
      }
      toast.error(formatMessage({ id: 'shareInvitationCodeGeneralError' }));
    }
  }, [formatMessage, shareData, userInvitationCode]);

  useEffect(() => {
    if (!userInvitationCode) {
      navigate(routes.base);
    }
  }, [userInvitationCode, navigate]);

  return featureInvitationCodeLoading ? (
    <LoadingAnimation fillColor={theme.token('icon-default')} />
  ) : (
    <AccountLayout header={featureInvitationCode?.invitationCodeTitle?.locale}>
      <BackArrowHistory />
      <InvitationCodeNumberContainer>
        <InvitationCodeNumberText>{userInvitationCode}</InvitationCodeNumberText>
        <CopyToClipboard text={userInvitationCode} onCopy={handleCopyInvitationCode}>
          <InvitationCodeNumberCopyIcon />
        </CopyToClipboard>
      </InvitationCodeNumberContainer>

      {featureInvitationCode?.invitationCodeInformationCards?.map((card, index) => (
        <InformationCard key={card?.cardTitle?.locale} data-testid={`information-card-${index}`}>
          <InformationCardImageWrapper $bgColor={card?.cardImageBackgroundColor?.hex}>
            <InformationCardImage image={card?.cardImage} alt="" />
          </InformationCardImageWrapper>
          <InformationCardTextsContainer>
            <InformationCardTitle>{card?.cardTitle?.locale}</InformationCardTitle>
            <CardDescription content={card?.cardDescription?.locale} />
          </InformationCardTextsContainer>
        </InformationCard>
      ))}
      {canShare && (
        <ShareMyCodeButton onClick={handleShareInvitationCode}>
          {featureInvitationCode?.shareMyCodeButtonText?.locale}
        </ShareMyCodeButton>
      )}

      <TermsAndConditions content={featureInvitationCode?.termsAndConditions?.locale} />
    </AccountLayout>
  );
};

export default withAuthRequired(AccountIvitationCode);
